import type { ValueProvenance } from "@sts/calc";
import type { ProvenancedValue } from "../api/types.js";

/**
 * How a measurement is presented according to where it came from.
 *
 * This is the product's argument, made visible. A heat loss is a sum of
 * numbers, some of which were measured and some of which were assumed, and the
 * assumed ones dominate the error: the BRE in-situ work found solid walls
 * averaging about 1.6 W/m²K against the RdSAP-style assumption of 2.1, an error
 * far larger than anything a LiDAR scan contributes. Every other tool prints
 * one undifferentiated table. This one has to make the difference legible
 * without the reader having to think about it.
 *
 * Three classes, because three is what a person can hold at a glance:
 *
 *  - `confirmed` — a person looked at this and accepted it. The strongest
 *    evidence in the system, whatever its original source.
 *  - `measured` — it came off an instrument. Nobody has signed for it, but
 *    nobody guessed it either.
 *  - `assumed` — it came from an age band, an EPC, or arithmetic over other
 *    assumptions. This is where a heat loss goes wrong.
 */
export type ProvenanceClass = "confirmed" | "measured" | "assumed";

const MEASURED: readonly ValueProvenance[] = [
  "scanned",
  "laser-measured",
  "surveyor-entered",
  "manufacturer",
];

export function classify(value: ProvenancedValue): ProvenanceClass {
  if (value.confirmedBySurveyor) return "confirmed";
  return MEASURED.includes(value.provenance) ? "measured" : "assumed";
}

/** Short label for the chip beside the number. */
export const PROVENANCE_LABEL: Record<ValueProvenance, string> = {
  scanned: "Scanned",
  "laser-measured": "Laser",
  "surveyor-entered": "Entered",
  epc: "EPC",
  // "Default", not "Age band": the same provenance carries the platform's
  // placeholder U-values and air change rates for a scanned survey
  // (apps/api/src/survey/scan-conversion.ts), which come from no age band.
  "age-band-default": "Default",
  manufacturer: "Datasheet",
  derived: "Derived",
};

/**
 * The full sentence, for the chip's title and for screen readers. A chip
 * reading "Age band" is only meaningful to somebody who already knows what the
 * product does.
 */
export const PROVENANCE_MEANING: Record<ValueProvenance, string> = {
  scanned: "Measured from the LiDAR scan of the room.",
  "laser-measured": "Measured on site with a laser distance meter.",
  // Two ways a figure gets here, and both are the surveyor's own hand: typed
  // into the survey on site, or drawn on a plan they drew because there was no
  // scan (migration 0047). Neither was measured by an instrument.
  "surveyor-entered": "Entered by the surveyor: typed on site, or drawn by hand on the plan.",
  epc: "Taken from the property's EPC record, not measured on site.",
  "age-band-default":
    "An assumed default: a construction age-band figure, or the platform's placeholder for a " +
    "scanned survey (its scan conversion records which set and why). Nobody has measured this.",
  manufacturer: "Taken from the product's datasheet.",
  derived: "Calculated from other values, which carry their own provenance.",
};

export const CLASS_MEANING: Record<ProvenanceClass, string> = {
  confirmed: "A surveyor has reviewed and accepted this value.",
  measured: "Measured, but not yet reviewed by a surveyor.",
  assumed: "Assumed rather than measured, and not yet reviewed.",
};

export function describe(value: ProvenancedValue): string {
  const meaning = PROVENANCE_MEANING[value.provenance] ?? "Unrecorded source.";
  const confirmation = value.confirmedBySurveyor
    ? " Confirmed by the surveyor."
    : " Not confirmed by a surveyor.";
  const confidence =
    value.confidence === null
      ? ""
      : ` Capture confidence ${Math.round(value.confidence * 100)}%.`;
  return meaning + confirmation + confidence;
}

/** True when this value is an unreviewed assumption — the thing to chase. */
export function isUnconfirmedAssumption(value: ProvenancedValue): boolean {
  return classify(value) === "assumed";
}

export interface ProvenanceTally {
  readonly confirmed: number;
  readonly measured: number;
  readonly assumed: number;
  readonly total: number;
}

export function tally(
  values: readonly ProvenancedValue[],
): ProvenanceTally {
  let confirmed = 0;
  let measured = 0;
  let assumed = 0;
  for (const value of values) {
    const klass = classify(value);
    if (klass === "confirmed") confirmed++;
    else if (klass === "measured") measured++;
    else assumed++;
  }
  return { confirmed, measured, assumed, total: values.length };
}
